import React, { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import wo from './overlays.module.css'
import { useLayer } from '../../components/layers'
import { apiJson } from '../../lib/api'
import { PendingButton, useSlow, inertClass } from '../../components/pending'
import { dmy, plural } from './format'
import type { QueueSummary } from './types'

// The recalculation slide-out (V4 wgQPanel, §7h.0a): the queued worker/date
// targets, each reprocessable on its own, while the table keeps showing the
// last valid basis.

interface Props {
  user: any
  queue: QueueSummary
  onClose: () => void
  onQueueMore: () => void
  onDone: (msg: string) => void
}

const STATE: Record<string, { text: string; cls: string }> = {
  queued: { text: 'Queued', cls: wo.chipQueued },
  processing: { text: 'Updating', cls: wo.chipUpdating },
  error: { text: 'Error', cls: wo.chipError },
}

export function QueuePanel({ user, queue, onClose, onQueueMore, onDone }: Props) {
  // 'failed', or the one target being reprocessed: only that button says so.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const n = queue.items.length
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape and the scrim close it. Nothing closes it
  // while a reprocess is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: panelEl, modal: true, busy })

  const run = async (on: string, body: unknown) => {
    setBusyOn(on)
    try {
      const r = await apiJson<{ message: string }>(user, '/api/wages/queue/process', { body })
      onDone(r.message)
    } catch (e: any) {
      onDone(e?.message || 'Could not reprocess')
    } finally {
      setBusyOn(null)
    }
  }

  return (
    <>
      <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <aside ref={panelEl} tabIndex={-1} className={`${o.drawer} ${wo.qDrawer}`} role="dialog" aria-label="Recalculation queue">
          <div className={`${o.drawerHead} ${wo.compactHead}`}>
            <div className={o.headText}>
              <h2 className={`${o.drawerTitle} ${wo.qTitle}`}>{plural(n, 'worker/day', 'worker/days')} queued for recalculation</h2>
              <p className={`${o.drawerSub} ${wo.qSub}`}>
                Wages below are calculated from the last valid basis. Only these targets are rebuilt
                {queue.failed ? `: ${queue.failed} failed and kept the previous valid basis.` : '.'}
              </p>
            </div>
            <button type="button" className={`${o.close} ${wo.qClose}`} aria-label="Close" onClick={requestClose} disabled={busy}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <div className={`${o.drawerBody} ${wo.qBody} ${inertClass(busy, slow)}`}>
            {n === 0 && <div className={wo.qEmpty}>Nothing is queued. The wage basis is current.</div>}
            {queue.items.map((q) => {
              const st = STATE[q.state] || STATE.queued
              return (
                <div key={q.human_id + q.work_date} className={wo.qRow}>
                  <div className={wo.qHead}>
                    <span className={wo.qWorker}>{q.worker_name}</span>
                    <span className={wo.qDate}>{dmy(q.work_date)}</span>
                    <span className={`${wo.chip} ${st.cls}`}>{st.text}</span>
                  </div>
                  {q.reason && <div className={wo.qReason}>{q.reason}</div>}
                  {q.state === 'error' && <div className={wo.qErr}>{q.error || 'Failed'}: the previous valid basis is still in use</div>}
                  <PendingButton type="button" className={wo.rowBtn} disabled={busy} busy={busyOn === q.human_id + q.work_date} pending="Reprocessing…" onClick={() => run(q.human_id + q.work_date, { targets: [{ human_id: q.human_id, work_date: q.work_date }] })}>
                    Reprocess this target
                  </PendingButton>
                </div>
              )
            })}
          </div>
          <div className={`${o.drawerFoot} ${wo.qFoot}`}>
            <PendingButton type="button" className={`${o.primary} ${wo.qFootBtn}`} disabled={!queue.failed || busy} busy={busyOn === 'failed'} pending="Reprocessing…" onClick={() => run('failed', { states: ['error'] })} title={queue.failed ? '' : 'No target has failed'}>
              Reprocess failed
            </PendingButton>
            <button type="button" className={`${o.ghost} ${wo.qFootBtn}`} onClick={onQueueMore} disabled={busy}>
              Queue more…
            </button>
          </div>
        </aside>
      </div>
      {confirm}
    </>
  )
}
